import React, { useState, useEffect } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend,
  LineChart, Line, CartesianGrid,
} from 'recharts';
import { getAnalyticsCharts } from '../../services/api';
import './DashboardCommon.css';
import smLogo from '../../assets/SM_logo-removebg.png';
import ClientList from '../ClientList';
import UserManagement from '../UserManagement';
import BrandList from '../BrandList';
import TenderList from '../TenderList';
import ParticipatingTenderList from '../ParticipatingTenderList';
import NoGoTenderList from '../NoGoTenderList';
import MasterTenderList from '../MasterTenderList';
import EmdList from '../EmdList';
import AllAuditTrailList from '../AllAuditTrailList';
import TenderKanban from '../TenderKanban';
import UpcomingDeadlinesWidget from '../UpcomingDeadlinesWidget';
import { BidValueStatsRow, PipelineValueChart, WinRateBySourceChart, WinRateByBuyerChart } from '../BidValueWidgets';
import ArchivedTenderList from '../ArchivedTenderList';
import MyAssignedTasks from '../MyAssignedTasks';
import ThemeToggle from '../ThemeToggle';
import NotificationBell from '../NotificationBell';

const CHART_COLORS = ['#b60000', '#10b981', '#f59e0b', '#3b82f6', '#6b7280', '#8b5cf6'];

function SuperAdminDashboard({ user, onLogout }) {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeMenu, setActiveMenu] = useState('dashboard');
  const [profileDropdown, setProfileDropdown] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState(['tenderManagement']);
  const [dashboardData, setDashboardData] = useState({ stats: null, charts: null });
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [dashboardError, setDashboardError] = useState(null);

  useEffect(() => {
    if (activeMenu !== 'dashboard') return;
    let cancelled = false;
    setDashboardLoading(true);
    setDashboardError(null);
    getAnalyticsCharts()
      .then((res) => {
        if (!cancelled && res.success) {
          setDashboardData({ stats: res.stats || {}, charts: res.charts || {} });
        }
      })
      .catch((err) => { if (!cancelled) setDashboardError(err.message); })
      .finally(() => { if (!cancelled) setDashboardLoading(false); });
    return () => { cancelled = true; };
  }, [activeMenu]);

  const menuItems = [
    { id: 'dashboard', label: 'Dashboard', icon: 'home' },
    { 
      id: 'tenderManagement', 
      label: 'Tender Management', 
      icon: 'file',
      children: [
        { id: 'pipeline', label: 'Pipeline Board', icon: 'columns' },
        { id: 'tenders', label: 'Tender Discovery', icon: 'search' },
        { id: 'participating', label: 'Participating', icon: 'check' },
        { id: 'won', label: 'Won', icon: 'trophy' },
        { id: 'lost', label: 'Lost', icon: 'x' },
        { id: 'dropped', label: 'Dropped', icon: 'minus' },
        { id: 'cancelled', label: 'Cancelled', icon: 'slash' },
        { id: 'master-list', label: 'Master List', icon: 'list' },
        { id: 'emds', label: 'EMDs', icon: 'briefcase' },
        { id: 'nogo', label: 'No Go Tenders', icon: 'x' },
        { id: 'archived', label: 'Archived', icon: 'archive' },
      ]
    },
    { id: 'myTasks', label: 'My Tasks', icon: 'checkSquare' },
    { id: 'clients', label: 'Client List', icon: 'building' },
    { id: 'brands', label: 'Brands & Products', icon: 'package' },
    { id: 'users', label: 'User Management', icon: 'users' },
    { id: 'auditTrail', label: 'Audit Trail', icon: 'activity' },
  ];

  const toggleMenuExpand = (menuId) => {
    setExpandedMenus(prev => 
      prev.includes(menuId) 
        ? prev.filter(id => id !== menuId)
        : [...prev, menuId]
    );
  };

  const getPageInfo = () => {
    if (activeMenu === 'dashboard') return { breadcrumb: [], title: 'Dashboard' };
    for (const item of menuItems) {
      if (item.children) {
        const child = item.children.find(c => c.id === activeMenu);
        if (child) return { breadcrumb: [item.label, child.label], title: child.label };
      } else if (item.id === activeMenu) {
        return { breadcrumb: [item.label], title: item.label };
      }
    }
    return { breadcrumb: [], title: '' };
  };

  const pageInfo = getPageInfo();

  const statsFromApi = dashboardData.stats;
  const stats = statsFromApi
    ? [
        { label: 'Total Users', value: String(statsFromApi.total_users ?? '–'), change: 'Active', color: '#b60000' },
        { label: 'Participating Tenders', value: String(statsFromApi.tender_participating ?? '–'), change: 'Active', color: '#10b981' },
        { label: 'Tender Discovery', value: String(statsFromApi.tender_discovery ?? '–'), change: 'Stage', color: '#f59e0b' },
        { label: 'Upcoming Deadlines', value: String(statsFromApi.upcoming_deadlines ?? '–'), change: '7 days', color: '#3b82f6' },
      ]
    : [
        { label: 'Total Users', value: '–', change: 'Active', color: '#b60000' },
        { label: 'Participating Tenders', value: '–', change: 'Active', color: '#10b981' },
        { label: 'Tender Discovery', value: '–', change: 'Stage', color: '#f59e0b' },
        { label: 'Upcoming Deadlines', value: '–', change: '7 days', color: '#3b82f6' },
      ];

  const renderIcon = (name) => {
    const icons = {
      home: <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>,
      search: <><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></>,
      check: <><polyline points="20 6 9 17 4 12"></polyline></>,
      x: <><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></>,
      file: <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><polyline points="10 9 9 9 8 9"></polyline></>,
      chevronDown: <polyline points="6 9 12 15 18 9"></polyline>,
      chevronRight: <polyline points="9 18 15 12 9 6"></polyline>,
      users: <><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M23 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></>,
      briefcase: <><rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path></>,
      building: <><rect x="4" y="2" width="16" height="20" rx="2" ry="2"></rect><path d="M9 22v-4h6v4"></path><path d="M8 6h.01"></path><path d="M16 6h.01"></path><path d="M12 6h.01"></path><path d="M12 10h.01"></path><path d="M12 14h.01"></path><path d="M16 10h.01"></path><path d="M16 14h.01"></path><path d="M8 10h.01"></path><path d="M8 14h.01"></path></>,
      package: <><line x1="16.5" y1="9.4" x2="7.5" y2="4.21"></line><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path><polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline><line x1="12" y1="22.08" x2="12" y2="12"></line></>,
      activity: <><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline></>,
      trophy: <><path d="M8 21h8"></path><path d="M12 17v4"></path><path d="M7 4h10v4a5 5 0 0 1-10 0V4z"></path><path d="M5 4H4a2 2 0 0 0-2 2v2a2 2 0 0 0 2 2h1"></path><path d="M19 4h1a2 2 0 0 1 2 2v2a2 2 0 0 1-2 2h-1"></path><path d="M7 8a5 5 0 0 0 10 0"></path></>,
      minus: <><line x1="5" y1="12" x2="19" y2="12"></line></>,
      slash: <><circle cx="12" cy="12" r="10"></circle><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"></line></>,
      list: <><line x1="8" y1="6" x2="21" y2="6"></line><line x1="8" y1="12" x2="21" y2="12"></line><line x1="8" y1="18" x2="21" y2="18"></line><line x1="3" y1="6" x2="3.01" y2="6"></line><line x1="3" y1="12" x2="3.01" y2="12"></line><line x1="3" y1="18" x2="3.01" y2="18"></line></>,
      columns: <><line x1="12" y1="3" x2="12" y2="21"></line><rect x="3" y="3" width="7" height="18" rx="1"></rect><rect x="14" y="3" width="7" height="18" rx="1"></rect></>,
      logout: <><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path><polyline points="16 17 21 12 16 7"></polyline><line x1="21" y1="12" x2="9" y2="12"></line></>,
      menu: <><line x1="3" y1="12" x2="21" y2="12"></line><line x1="3" y1="6" x2="21" y2="6"></line><line x1="3" y1="18" x2="21" y2="18"></line></>,
      bell: <><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path><path d="M13.73 21a2 2 0 0 1-3.46 0"></path></>,
      archive: <><polyline points="21 8 21 21 3 21 3 8"></polyline><rect x="1" y="3" width="22" height="5"></rect><line x1="10" y1="12" x2="14" y2="12"></line></>,
      checkSquare: <><polyline points="9 11 12 14 22 4"></polyline><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"></path></>,
    };
    return icons[name] || null;
  };

  const renderContent = () => {
    switch (activeMenu) {
      case 'pipeline':
        return <TenderKanban />;
      case 'tenders':
        return <TenderList user={user} canManage={true} />;
      case 'participating':
        return <ParticipatingTenderList user={user} canManage={true} onGoToDiscovery={() => setActiveMenu('tenders')} />;
      case 'won':
        return <ParticipatingTenderList user={user} canManage={true} fixedStatus="won" onGoToDiscovery={() => setActiveMenu('tenders')} />;
      case 'lost':
        return <ParticipatingTenderList user={user} canManage={true} fixedStatus="lost" onGoToDiscovery={() => setActiveMenu('tenders')} />;
      case 'dropped':
        return <ParticipatingTenderList user={user} canManage={true} fixedStatus="dropped" onGoToDiscovery={() => setActiveMenu('tenders')} />;
      case 'cancelled':
        return <ParticipatingTenderList user={user} canManage={true} fixedStatus="canceled" onGoToDiscovery={() => setActiveMenu('tenders')} />;
      case 'master-list':
        return <MasterTenderList user={user} canManage={true} onGoToDiscovery={() => setActiveMenu('tenders')} />;
      case 'emds':
        return <EmdList />;
      case 'nogo':
        return <NoGoTenderList user={user} canManage={false} onGoToDiscovery={() => setActiveMenu('tenders')} />;
      case 'archived':
        return <ArchivedTenderList />;
      case 'myTasks':
        return <MyAssignedTasks user={user} />;
      case 'clients':
        return <ClientList user={user} canEdit={true} />;
      case 'brands':
        return <BrandList user={user} canEdit={true} />;
      case 'users':
        return <UserManagement currentUser={user} />;
      case 'auditTrail':
        return <AllAuditTrailList />;
      case 'dashboard':
      default:
        return (
          <>
            <div className="content-header">
              <h2>Welcome back, {user?.full_name || user?.username || 'Admin'}!</h2>
              <p>Here's your system overview for today.</p>
            </div>

            <UpcomingDeadlinesWidget />

            {dashboardError && (
              <div className="error-message" style={{ marginBottom: 16 }}>
                {dashboardError}
                <button type="button" onClick={() => setDashboardError(null)}>×</button>
              </div>
            )}

            {/* Stats Grid */}
            <div className="stats-grid">
              {stats.map((stat, index) => (
                <div key={index} className="stat-card">
                  <div className="stat-content">
                    <span className="stat-label">{stat.label}</span>
                    <span className="stat-value">{dashboardLoading ? '…' : stat.value}</span>
                    <span className="stat-change" style={{ color: stat.color }}>{stat.change}</span>
                  </div>
                </div>
              ))}
            </div>

            {!dashboardLoading && dashboardData.stats && (
              <BidValueStatsRow stats={dashboardData.stats} loading={dashboardLoading} />
            )}

            {/* Charts */}
            {!dashboardLoading && dashboardData.charts && (
              <div className="charts-row" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 24, marginBottom: 24 }}>
                {dashboardData.charts.users_by_role && dashboardData.charts.users_by_role.length > 0 && (
                  <div className="chart-card">
                    <h3>Users by Role</h3>
                    <div style={{ height: 260 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={dashboardData.charts.users_by_role}
                            dataKey="count"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            outerRadius={80}
                            label={({ name, count }) => `${name}: ${count}`}
                          >
                            {dashboardData.charts.users_by_role.map((_, i) => (
                              <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                )}
                {dashboardData.charts.tenders_by_stage && dashboardData.charts.tenders_by_stage.length > 0 && (
                  <div className="chart-card">
                    <h3>Tenders by Stage</h3>
                    <div style={{ height: 260 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={dashboardData.charts.tenders_by_stage}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="name" />
                          <YAxis />
                          <Tooltip />
                          <Bar dataKey="count" fill="#b60000" name="Count" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                )}
                {dashboardData.charts.tenders_over_time && dashboardData.charts.tenders_over_time.length > 0 && (
                  <div className="chart-card" style={{ gridColumn: '1 / -1' }}>
                    <h3>Tenders Over Time (last 6 months)</h3>
                    <div style={{ height: 260 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={dashboardData.charts.tenders_over_time}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="month" />
                          <YAxis />
                          <Tooltip />
                          <Legend />
                          <Line type="monotone" dataKey="count" stroke="#b60000" name="Total" strokeWidth={2} dot={{ r: 4 }} />
                          <Line type="monotone" dataKey="participating" stroke="#10b981" name="Participating" strokeWidth={2} dot={{ r: 4 }} />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                )}
                {dashboardData.charts.audit_activity_by_day && dashboardData.charts.audit_activity_by_day.length > 0 && (
                  <div className="chart-card">
                    <h3>Audit activity (last 14 days)</h3>
                    <div style={{ height: 260 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={dashboardData.charts.audit_activity_by_day}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="date" />
                          <YAxis />
                          <Tooltip />
                          <Bar dataKey="count" fill="#6b7280" name="Actions" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                )}
                {dashboardData.charts.audit_actions_by_type && dashboardData.charts.audit_actions_by_type.length > 0 && (
                  <div className="chart-card">
                    <h3>Actions by type (last 14 days)</h3>
                    <div style={{ height: 260 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={dashboardData.charts.audit_actions_by_type} layout="vertical" margin={{ left: 80 }}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis type="number" />
                          <YAxis type="category" dataKey="name" width={70} />
                          <Tooltip />
                          <Bar dataKey="count" fill="#8b5cf6" name="Count" radius={[0, 4, 4, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                )}
                {dashboardData.charts.bid_value?.pipeline_value_by_status && (
                  <PipelineValueChart data={dashboardData.charts.bid_value.pipeline_value_by_status} />
                )}
                {dashboardData.charts.bid_value?.win_rate_by_source && (
                  <WinRateBySourceChart data={dashboardData.charts.bid_value.win_rate_by_source} />
                )}
                {dashboardData.charts.bid_value?.win_rate_by_buyer && (
                  <WinRateByBuyerChart data={dashboardData.charts.bid_value.win_rate_by_buyer} />
                )}
              </div>
            )}

            {/* Quick Actions */}
            <div className="placeholder-section">
              <div className="placeholder-card clickable" onClick={() => setActiveMenu('tenders')}>
                <h3>Tender Discovery</h3>
                <p>Discover and manage tenders from various sources.</p>
                <button className="placeholder-btn active">Manage Tenders</button>
              </div>
              <div className="placeholder-card clickable" onClick={() => setActiveMenu('clients')}>
                <h3>Client List</h3>
                <p>View and manage all registered companies and clients.</p>
                <button className="placeholder-btn active">View Clients</button>
              </div>
              <div className="placeholder-card clickable" onClick={() => setActiveMenu('users')}>
                <h3>User Management</h3>
                <p>Manage all system users, roles, and permissions.</p>
                <button className="placeholder-btn active">Manage Users</button>
              </div>
              <div className="placeholder-card clickable" onClick={() => setActiveMenu('auditTrail')}>
                <h3>Audit Trail</h3>
                <p>View all system activity in one place.</p>
                <button className="placeholder-btn active">View Audit Trail</button>
              </div>
            </div>
          </>
        );
    }
  };

  return (
    <div className="dashboard-layout">
      {/* Sidebar */}
      <aside className={`sidebar ${sidebarOpen ? 'open' : 'closed'}`}>
        <button className="sidebar-toggle" onClick={() => setSidebarOpen(!sidebarOpen)} data-tooltip={sidebarOpen ? 'Collapse' : 'Expand'}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            {sidebarOpen ? (
              <path d="M15 18l-6-6 6-6" />
            ) : (
              <path d="M9 18l6-6-6-6" />
            )}
          </svg>
        </button>

        <nav className="sidebar-nav">
          {menuItems.map((item) => (
            item.children ? (
              <div key={item.id} className="nav-group">
                <button
                  className={`nav-item nav-parent ${expandedMenus.includes(item.id) ? 'expanded' : ''} ${item.children.some(child => activeMenu === child.id) ? 'has-active' : ''}`}
                  onClick={() => toggleMenuExpand(item.id)}
                  data-tooltip={item.label}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    {renderIcon(item.icon)}
                  </svg>
                  <span>{item.label}</span>
                  <svg className="expand-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    {renderIcon(expandedMenus.includes(item.id) ? 'chevronDown' : 'chevronRight')}
                  </svg>
                </button>
                <div className={`nav-children ${expandedMenus.includes(item.id) ? 'expanded' : ''}`}>
                  {item.children.map((child) => (
                    <button
                      key={child.id}
                      className={`nav-item nav-child ${activeMenu === child.id ? 'active' : ''}`}
                      onClick={() => setActiveMenu(child.id)}
                      data-tooltip={child.label}
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        {renderIcon(child.icon)}
                      </svg>
                      <span>{child.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <button
                key={item.id}
                className={`nav-item ${activeMenu === item.id ? 'active' : ''}`}
                onClick={() => setActiveMenu(item.id)}
                data-tooltip={item.label}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  {renderIcon(item.icon)}
                </svg>
                <span>{item.label}</span>
              </button>
            )
          ))}
        </nav>
      </aside>

      {/* Top Bar - Full Width */}
      <header className="topbar">
        <div className="topbar-left">
          <img src={smLogo} alt="SM Systems" className="topbar-logo" />
          <div className="topbar-divider"></div>
          <h1 className="page-title">Super Admin Dashboard</h1>
        </div>
        
        <div className="topbar-right">
          <NotificationBell />
          <ThemeToggle />
          <div className="user-profile-wrapper">
            <button className="user-profile" onClick={() => setProfileDropdown(!profileDropdown)}>
              <div className="avatar">{(user?.full_name || user?.username)?.charAt(0) || 'A'}</div>
              <div className="user-info">
                <span className="user-name">{user?.full_name || user?.username || 'Admin'}</span>
                <span className="user-role">Super Administrator</span>
              </div>
              <svg className={`dropdown-arrow ${profileDropdown ? 'open' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M6 9l6 6 6-6" />
              </svg>
            </button>
            {profileDropdown && (
              <div className="profile-dropdown">
                <button className="dropdown-item" onClick={onLogout}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    {renderIcon('logout')}
                  </svg>
                  <span>Logout</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className={`main-content ${sidebarOpen ? '' : 'expanded'}`}>
        <div className="content">
          <div className="content-title-area">
            {pageInfo.breadcrumb.length > 0 && (
              <nav className="breadcrumb" aria-label="Breadcrumb">
                {pageInfo.breadcrumb.map((label, i) => (
                  <span key={i}>
                    {i > 0 && <span className="breadcrumb-sep"> / </span>}
                    <span>{label}</span>
                  </span>
                ))}
              </nav>
            )}
            <h2 className="content-page-title">{pageInfo.title}</h2>
          </div>
          {renderContent()}
        </div>
      </main>
    </div>
  );
}

export default SuperAdminDashboard;
